import { FC } from 'react';
import { logger } from '@belong/logging/logger';
import { BREAKPOINTS } from '@belong/themes';
import { IMAGE_ASPECT_RATIO, IMediaCaptionBlock } from '@belong/types';
import { getResponsiveSrc } from '../../../helpers/image';
import { PictureContainer, VideoContainer, VideoEmbed } from '../../media';
import { AspectRatioContainer, Caption, Container } from './styles';
import { multipleSources } from './utils';

export const TEST_ID = {
  container: 'media-caption-block',
  mediaElement: 'media-caption-block--media',
  caption: 'media-caption-block--caption'
};

const MediaElement: FC<IMediaCaptionBlock> = ({
  image,
  video,
  videoId,
  aspectRatio = IMAGE_ASPECT_RATIO.letterbox
}) => {
  if (image) {
    const src = getResponsiveSrc(image, {
      xs: { w: BREAKPOINTS.md },
      md: { w: BREAKPOINTS.lg }
    });

    const picture = <PictureContainer {...image} src={src} />;

    if (aspectRatio === IMAGE_ASPECT_RATIO.auto) {
      return picture;
    }

    return (
      <AspectRatioContainer data-testid={TEST_ID.mediaElement} ratio={aspectRatio}>
        {picture}
      </AspectRatioContainer>
    );
  }

  if (videoId) {
    return <VideoEmbed data-testid={TEST_ID.mediaElement} videoId={videoId} />;
  }

  if (video) {
    return <VideoContainer data-testid={TEST_ID.mediaElement} {...video} />;
  }

  return null;
};

MediaElement.displayName = 'MediaElement';

/**
 * Embeddable component to display either YouTube Video or an Image with an optional caption
 */
export const MediaCaptionBlock: FC<IMediaCaptionBlock> = props => {
  const { image, video, videoId, caption, aspectRatio } = props;
  const hasMedia = !!image || !!videoId || !!video;
  const hasMultipleSources = multipleSources(image, videoId, video);
  const hasAutoAspectRatio = aspectRatio === IMAGE_ASPECT_RATIO.auto;

  if (!hasMedia || hasMultipleSources) {
    logger.error('Please check media sources - can only render one source');
    return null;
  }

  return (
    <Container data-testid={TEST_ID.container} className="media-caption-block" limitImageWidth={hasAutoAspectRatio}>
      <MediaElement data-testid={TEST_ID.mediaElement} {...props} />
      {caption && <Caption data-testid={TEST_ID.caption}>{caption}</Caption>}
    </Container>
  );
};

MediaCaptionBlock.displayName = 'MediaCaptionBlock';

export default MediaCaptionBlock;
